import { updateStoreListUI } from '../ui/list';
import { syncTagsUI } from '../ui/tags';
import { DEFINITIONS } from '../utils/constants';
import { filterStoresByTags, getViewportStores } from '../utils/map';
import { setsAreEqual } from '../utils/stores';

/**
 * @description Applies active filters to the store list and updates the UI accordingly
 * Updates the store list, tags UI, and map markers based on the current state
 * Avoids unnecessary updates if the visible store IDs have not changed
 * @param {HTMLElement} context - The section container element
 * @param {Object} options - Configuration options
 * @param {Object} options.state - The application state object
 * @param {Object} options.map - The Google Map instance
 * @returns {void}
 */
export const applyFiltersAndUpdate = (context, { state, map }) => {
  // Get current bounds
  const currentBounds = map?.getBounds?.() ?? null;

  if (!currentBounds) {
    // Wait for map to be ready
    if (window.google?.maps?.event) {
      window.google.maps.event.addListenerOnce(map, 'idle', () => applyFiltersAndUpdate(context, { state, map }));
    }
    return;
  }

  // Stores visible in viewport BEFORE applying tags (for tags UI counts/visibility)
  const viewportStores = getViewportStores(map, state.allStores);

  // Apply tag filter to the viewport stores for the list rendering
  const filteredViewportStores = filterStoresByTags(viewportStores, state.activeTags);
  const visibleIds = new Set(filteredViewportStores.map((s) => String(s.uid ?? s.id)));

  // Check if visible stores have changed BEFORE updating
  if (setsAreEqual(visibleIds, state.lastVisibleIds) && visibleIds.size > 0) {
    // No changes in visible stores, skip all updates
    return;
  }

  // Update lastVisibleIds FIRST
  state.lastVisibleIds = visibleIds;

  // Extend-search button
  const extendSearchButton = context.querySelector(`.${DEFINITIONS.slug}__list-extender`);
  if (extendSearchButton) {
    extendSearchButton.onclick = () => {
      const bounds = new window.google.maps.LatLngBounds();

      state.allStores.forEach((store) => {
        const lat = Number(store.latitude);
        const lng = Number(store.longitude);

        bounds.extend(new window.google.maps.LatLng(lat, lng));
      });

      // Fit bounds with padding to avoid grey areas
      map.fitBounds(bounds, {
        padding: { top: 50, right: 50, bottom: 50, left: 50 },
      });

      // Ensure zoom is not too extreme
      setTimeout(() => {
        const currentZoom = map.getZoom();
        if (currentZoom > 18) {
          map.setZoom(18);
        } else if (currentZoom < 3) {
          map.setZoom(3);
        }
      }, 100);
    };
  }

  // Update the list UI with current viewport stores
  updateStoreListUI(context, {
    allStores: state.allStores,
    viewportStores: filteredViewportStores,
    tags: state.tags,
    map,
  });

  // Keep tags UI in sync with current viewport and selection
  syncTagsUI(context, {
    allTags: state.tags,
    activeTags: state.activeTags,
    viewportStores,
  });

  // Update markers visibility (clusterer handles the actual rendering)
  if (state.clusterer) {
    // Get visible marker instances
    const visibleMarkers = Object.entries(state.markers)
      .filter(([id]) => visibleIds.has(String(id)))
      .map(([, marker]) => marker);

    // Only update if the marker count has changed
    const currentMarkerCount = state.clusterer.markers?.length || 0;
    if (currentMarkerCount !== visibleMarkers.length) {
      state.clusterer.clearMarkers();
      state.clusterer.addMarkers(visibleMarkers);
    }
    // If count is the same, clusterer will handle viewport changes internally
  } else {
    // No clusterer: toggle marker visibility by map property
    Object.entries(state.markers).forEach(([storeId, marker]) => {
      const shouldShow = visibleIds.has(String(storeId));
      marker.map = shouldShow ? map : null;
    });
  }
};
